import {Box, Divider, Paper, Skeleton, Stack, Typography} from "@mui/material";
import {AggregationSurveyIntroductionSurvey} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyFormat, SurveyStatus, SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import {formatDateTime} from "crqlar-react-common/dist/utils/date.util";
import {memo, useMemo} from "react";
import {useTranslation} from "react-i18next";

export const SURVEY_TYPE_LABELS: Record<SurveyType, string> = {
    [SurveyType.PRE_STAY]: "Pre stay",
    [SurveyType.POST_STAY]: "Post stay",
    [SurveyType.PRE_SERVICE]: "Pre service",
    [SurveyType.POST_SERVICE]: "Post service",
    [SurveyType.ANONYMOUS]: "Anonymous",
};

const SURVEY_FORMAT_LABELS: Record<SurveyFormat, string> = {
    [SurveyFormat.SINGLE_PAGE]: "All on one page",
    [SurveyFormat.MULTI_PAGE]: "One question per page",
    [SurveyFormat.SECTIONS]: "One section per page",
};

const UNKNOWN = "—";
const FACT_COLUMNS = 3;

type Translate = (key: string) => string;

interface Fact {
    label: string;
    value: string;
}

const isPublished = (survey: AggregationSurveyIntroductionSurvey) =>
    survey.status === SurveyStatus.PUBLISHED && survey.version > 0;

const subtitleOf = (survey: AggregationSurveyIntroductionSurvey, t: Translate) =>
    [survey.type ? t(SURVEY_TYPE_LABELS[survey.type]) : "", survey.sectorNames.join(", ")]
        .filter(Boolean)
        .join(" · ");

const factsOf = (survey: AggregationSurveyIntroductionSurvey | undefined, t: Translate): Fact[] => {
    const published = survey != null && isPublished(survey);

    return [
        {
            label: t("Status"),
            value: survey == null ? UNKNOWN : published ? t("Published") : t("Draft"),
        },
        {
            label: t("Published At"),
            value: published && survey.publishedAt != null ? formatDateTime("dd.MM.yyyy", survey.publishedAt) : t("Never"),
        },
        {
            label: t("Version"),
            value: published ? `v${survey.version}` : UNKNOWN,
        },
        {
            label: t("Questions"),
            value: survey == null ? UNKNOWN : String(survey.questionCount),
        },
        {
            label: t("Format"),
            // Saved without a format, a survey runs as a single page.
            value: survey == null ? UNKNOWN : t(SURVEY_FORMAT_LABELS[survey.format ?? SurveyFormat.SINGLE_PAGE]),
        },
        {
            label: t("Retention"),
            value: survey?.retentionDays != null ? `${survey.retentionDays} ${t("days")}` : UNKNOWN,
        },
    ];
};

interface FactCellProps extends Fact {
    loading: boolean;
}

function FactCell({label, value, loading}: FactCellProps) {
    return (
        <Stack gap={0.25} sx={{minWidth: 0}}>
            <Typography variant={"body1"} color={"text.secondary"} fontWeight={600} noWrap>
                {label}
            </Typography>

            <Typography variant={"h6"} component={"div"} sx={{fontWeight: 700}} noWrap>
                {loading ? <Skeleton animation={"wave"} width={"56%"}/> : value}
            </Typography>
        </Stack>
    );
}

interface Props {
    survey?: AggregationSurveyIntroductionSurvey;
    /** The survey's colour: a dot after its name, as the survey picker shows it. */
    color?: string;
    loading?: boolean;
}

export const SurveyFactsCard = memo(function SurveyFactsCard({survey, color, loading = false}: Props) {
    const {t} = useTranslation();

    const subtitle = useMemo(() => (survey == null ? "" : subtitleOf(survey, t)), [survey, t]);
    const facts = useMemo(() => factsOf(survey, t), [survey, t]);

    return (
        <Paper
            variant={"outlined"}
            sx={{
                width: "100%",
                height: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                px: 3,
                py: 3.5,
                borderRadius: 2,
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Stack gap={0.5}>
                {loading ? (
                    <Typography variant={"h5"}>
                        <Skeleton animation={"wave"} width={"46%"}/>
                    </Typography>
                ) : (
                    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{minWidth: 0}}>
                        <Typography variant={"h5"} sx={{fontWeight: 700, minWidth: 0}} noWrap>
                            {survey?.name || UNKNOWN}
                        </Typography>
                        {color && survey != null && (
                            <Box aria-hidden sx={{width: 14, height: 14, flexShrink: 0, borderRadius: "50%", bgcolor: color}}/>
                        )}
                    </Stack>
                )}

                <Typography sx={{color: "text.secondary"}} noWrap>
                    {loading ? <Skeleton animation={"wave"} width={"32%"}/> : (subtitle || UNKNOWN)}
                </Typography>
            </Stack>

            <Divider sx={{my: 3}}/>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${FACT_COLUMNS}, minmax(0, 1fr))`,
                    columnGap: 2,
                    rowGap: 3,
                }}
            >
                {facts.map(fact => (
                    <FactCell key={fact.label} label={fact.label} value={fact.value} loading={loading}/>
                ))}
            </Box>
        </Paper>
    );
});
